import { Component, inject, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MasterService } from "@frontend/app/serivces/master.service";
import { ToastrService } from "@frontend/app/serivces/toastr.service";
import { SharedModule } from "@frontend/app/shared/shared.module";
import { MultiSelectDropdownComponent } from "@frontend/app/components/multi-select-dropdown/multi-select-dropdown.component";
import { MatTableDataSource } from "@angular/material/table";
import { SelectionModel } from "@angular/cdk/collections";
import { SVG_ICONS } from "../../../models/icons";
import { ActivatedRoute } from "@angular/router";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { EventRegConfirmComponent } from "../event-reg-confirm/event-reg-confirm.component";

@Component({
  selector: "app-event-reg",
  imports: [CommonModule, SharedModule, MultiSelectDropdownComponent, MatDialogModule],
  templateUrl: "./event-reg.component.html",
  styleUrl: "./event-reg.component.scss",
})
export class EventRegComponent implements OnInit {
  private masterService = inject(MasterService);
  private toastService = inject(ToastrService);
  private activatedRoute = inject(ActivatedRoute);
  private dialog = inject(MatDialog);

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe(params => {
      this.eventId = params["eventId"];
    });
    this.getStudentList();
    this.getCenterList();
    this.getClassList();
    this.getEventDetailsByID();
  }

  //#region variables
  studentList: any;
  filteredStudentList: any;
  centerList: any;
  eventDetails: any;
  classList: any;
  selectedCenters: any[] = [];
  selectedClasses: any[] = [];
  registeredStudentIDs: number[] = [];
  fullRegistrationList: any[] = [];
  displayedColumns: string[] = ["select", "status", "FirstName", "Gender", "MobileNo", "DOB", "Height", "Weight", "Email", "CenterName", "ClassName", "Address"];
  dataSource = new MatTableDataSource();
  selection = new SelectionModel<any>(true, []);
  length = 0;
  readonly SVG_ICONS = SVG_ICONS;
  eventId = 0;
  //#endregion

  getStudentList() {
    const storedIDs = sessionStorage.getItem("AllCenterIDs");
    const centerIDs: any[] = storedIDs ? JSON.parse(storedIDs) : [];
    const centerIDsString = centerIDs.join(",");

    this.masterService.getStudentList(1, 10000, "asc", centerIDsString, "", "", "").subscribe((res: any) => {
      this.studentList = res.Table || [];
      this.filterData();
      this.applyPreSelection();
    });
  }

  getCenterList() {
    this.masterService.getCenterList().subscribe((res: any) => {
      const storedIDs = sessionStorage.getItem("AllCenterIDs");
      const centerIDs: any[] = storedIDs ? JSON.parse(storedIDs) : [];
      this.centerList = (res.data || res.Table || []).filter((item: any) => centerIDs.map(id => String(id)).includes(String(item.CenterID)));
    });
  }

  getClassList() {
    this.masterService.getClassList(1, 1000, "desc", sessionStorage.getItem("selectedCenter") || "", "", sessionStorage.getItem("TrainerID") || "", "", "").subscribe((res: any) => {
      this.classList = res.Table || [];
    });
  }

  getEventDetailsByID() {
    this.masterService.getEventRegistrationListByID(this.eventId).subscribe((res: any) => {
      const data = res.data || res.Table || (Array.isArray(res) ? res : []);
      if (data) {
        this.fullRegistrationList = data;
        this.registeredStudentIDs = data.filter((reg: any) => reg.IsActive !== false).map((reg: any) => reg.StudentID);

        // Pre-populate filters based on active registrations
        const activeRegs = data.filter((reg: any) => reg.IsActive !== false);
        if (activeRegs.length > 0) {
          const centerIDs = [...new Set(activeRegs.map((reg: any) => reg.CenterID))];
          const classIDs = [...new Set(activeRegs.map((reg: any) => reg.ClassID))];

          this.selectedCenters = centerIDs;
          // this.selectedClasses = classIDs;
          this.filterData();
        }

        this.applyPreSelection();
      }
    });
  }

  applyPreSelection() {
    if (this.registeredStudentIDs.length > 0 && this.studentList && this.studentList.length > 0) {
      this.studentList.forEach((student: any) => {
        if (this.registeredStudentIDs.includes(student.StudentID)) {
          this.selection.select(student);
        }
      });
    }
  }

  filterData() {
    if (!this.studentList) return;

    const hasCenters = this.selectedCenters && this.selectedCenters.length > 0;
    const hasClasses = this.selectedClasses && this.selectedClasses.length > 0;

    const selCenterIDs = this.selectedCenters?.map((id: any) => String(id)) || [];
    const selClassIDs = this.selectedClasses?.map((id: any) => String(id)) || [];

    this.filteredStudentList = this.studentList.filter((student: any) => {
      const studentCenterID = String(student.CenterID || student.center?.CenterID || student.centerID || "");
      const centerMatch = !hasCenters || selCenterIDs.includes(studentCenterID);

      const studentClassID = String(student.CurrentActivePackage?.ClassID || student.ClassID || student.ClassId || "");
      const enrollmentMatch = student.classEnrollments?.some((e: any) => selClassIDs.includes(String(e.ClassID))) || false;
      const classMatch = !hasClasses || selClassIDs.includes(studentClassID) || enrollmentMatch;

      return centerMatch && classMatch;
    });

    this.dataSource.data = this.filteredStudentList;
  }

  isRegistered(studentId: number): boolean {
    return this.registeredStudentIDs.includes(studentId);
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    if (!this.dataSource.data.length) return false;
    return this.dataSource.data.every(row => this.selection.isSelected(row));
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    if (this.isAllSelected()) {
      this.dataSource.data.forEach(row => this.selection.deselect(row));
    } else {
      this.dataSource.data.forEach(row => this.selection.select(row));
    }
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  onSubmit() {
    const selectedStudents = this.selection.selected;
    if (selectedStudents.length === 0) {
      this.toastService.warning("Please select at least one student.");
      return;
    }

    const dialogRef = this.dialog.open(EventRegConfirmComponent, {
      width: "500px",
      data: { students: selectedStudents },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.submitRegistration();
      }
    });
  }

  submitRegistration() {
    const selectedStudentIDs = this.selection.selected.map((s: any) => s.StudentID);

    const eventLists = this.selection.selected.map((student: any) => {
      let classId = 0;
      if (this.selectedClasses && this.selectedClasses.length > 0) {
        const enrollment = student.classEnrollments?.find((e: any) => this.selectedClasses.includes(e.ClassID));
        classId = enrollment ? enrollment.ClassID : 0;
      } else {
        classId = student.CurrentActivePackage?.ClassID || student.classEnrollments?.[0]?.ClassID || 0;
      }

      return {
        CenterID: student.CenterID,
        ClassID: classId,
        StudentID: student.StudentID,
        RegistrationDate: new Date().toISOString(),
        PaymentStatus: "Paid",
        PaymentAmount: 100,
        IsApproved: true,
        IsActive: true,
      };
    });

    // Add previously registered students who are now unselected with IsActive: false
    if (this.fullRegistrationList && this.fullRegistrationList.length > 0) {
      this.fullRegistrationList.forEach((reg: any) => {
        if (!selectedStudentIDs.includes(reg.StudentID)) {
          eventLists.push({
            CenterID: reg.CenterID,
            ClassID: reg.ClassID,
            StudentID: reg.StudentID,
            RegistrationDate: reg.RegistrationDate || new Date().toISOString(),
            PaymentStatus: reg.PaymentStatus || "Paid",
            PaymentAmount: reg.PaymentAmount || 100,
            IsApproved: reg.IsApproved ?? true,
            IsActive: false,
          });
        }
      });
    }

    const payload = {
      EventID: Number(this.eventId),
      EventLists: eventLists,
    };

    if (this.registeredStudentIDs.length > 0) {
      this.masterService.updateEventRegistration(payload, Number(this.eventId)).subscribe((res: any) => {
        this.toastService.success("Registration Updated Successfully");
        this.selection.clear();
        this.getEventDetailsByID();
      });
    } else {
      this.masterService.addEventRegistration(payload).subscribe((res: any) => {
        this.toastService.success("Registration Successful");
        this.selection.clear();
        this.getEventDetailsByID();
      });
    }
  }

  onClose() {
    // this.dialogRef.close();
  }
}
